import axios from 'axios';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, BrowserRouter as Router, Routes, useLocation } from 'react-router-dom';
import ForgotPassword from "./pages/ForgotPassword";
import InternalSignInPage from "./pages/InternalSignInPage";
import SignInPage from "./pages/SignInPage";
import SignUpPage from "./pages/SignUpPage";
import * as userActions from './redux/User/actions';
import { ContextProvider } from "./utils/ContextApi";
import { ext } from './utils/general';
import * as ROUTES from './utils/routes';

import Home from "./pages/Home";
import Data from "./pages/Home/data";
import Partnership from "./pages/Home/partnership";
import Sales from "./pages/Home/sales";
import Settings from "./pages/Settings";
import DBSnapshots from './pages/Settings/DBSnapshots';
import LicenseManagement from './pages/Settings/License';
import UserManagement from './pages/Settings/Users';
import SalesforceManagers from './pages/Settings/SalesforceManagers';

import ToastManager from "./components/ToastManager";
import Accounts from "./pages/Accounts";
import Mapping from "./pages/Mapping";
import constants from "./utils/constants";
import { isAdmin, signedUserData } from "./utils/user";

import Contacts from "./pages/Contacts";

import Opportunities from "./pages/Opportunities";
import ViewOpportunity from './pages/Opportunities/ViewOpportunity';

import CoSellOpportunities from "./pages/CoSellOpportunities";
import ViewCoSellOpportunity from './pages/CoSellOpportunities/ViewCoSellOpportunity';

import Heatmaps from "./pages/Heatmaps";
import ViewHeatmap from './pages/Heatmaps/ViewHeatmap';

import Campaigns from "./pages/Campaigns";
import CampaignMembers from './pages/Campaigns/CampaignMembers';
import ViewCampaign from './pages/Campaigns/ViewCampaign';

import Programs from "./pages/Programs";
import EditPrograms from './pages/Programs/EditProgram';

import Funds from "./pages/Funds";
import EditFunds from './pages/Funds/EditFunds';

import Marketing from "./pages/Marketing";
import EditMarketing from './pages/Marketing/EditMarketing';

import "react-datepicker/dist/react-datepicker.css";
import 'react-toastify/dist/ReactToastify.css';
import ErrorPage from "./pages/ErrorPage";
import DashboardReport from "./pages/Home/DashboardReport";

import './App.css';
import EntityEditor from './components/EntityEditor/index';
import InactivityScreen from './components/InactivityScreen';
import BI from './pages/BI';
import Integrations from './pages/Integrations';
import HubspotIntegration from './pages/Integrations/HubspotIntegration';
import SalesforceIntegration from './pages/Integrations/SalesforceIntegration';
import Test from "./pages/Settings/Test";

const log = require("loglevel").getLogger("App");
log.setLevel("error");

axios.interceptors.response.use(
	response => response,
	error => {
		if (error.response && error.response.status === 403) {
			localStorage.removeItem(constants.STORAGE_SIGNED_USER_KEY);
			axios.defaults.headers.common['Connact-Access'] = null;
			// Redirect to login page
			window.location.href = '/home';
			return new Promise(() => { });
		} else {
			return Promise.reject(error);
		}
	}
);

const ProtectedRouteRedirect = () => {
	useEffect(() => {
		localStorage.removeItem(constants.MENU_DATA);
	}, []);
	return <Navigate to={ROUTES.HOME} />;
};

const App = () => {
	const user = useSelector(state => state.user.user);
	const dispatch = useDispatch();
	const isAdminUser = isAdmin();

	useEffect(() => {
		// console.log("process.env.NODE_ENV",process.env.NODE_ENV);
		// if(process.env.NODE_ENV === 'development' && user == null) {
		if(user == null) {
			const storageUser = signedUserData();

			if(storageUser) {
				dispatch(userActions.setLoggedInUser(JSON.parse(storageUser)));
			}
		}
	}, [dispatch, user]);

	if(!user) {
		return (
			<>
				<ToastManager />
				<Router>
					<Routes>
						<Route path={ROUTES.INTERNAL_SIGN_IN} element={<InternalSignInPage />} />
						<Route path={ROUTES.SIGN_IN} element={<SignInPage />} />
						<Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
						<Route path={ROUTES.ERROR} element={<ErrorPage />} />
						<Route path={ROUTES.INVITE} element={<SignUpPage />} />
						<Route path="*" element={<ConditionalSignin />} />
					</Routes>
				</Router>
			</>
		)
	}

	function ConditionalSignin() {
		const location = useLocation();
		const path = location.pathname;

		// console.log("PATH = ", path);

		return (
			<Navigate to={path === '/internal' ? ROUTES.INTERNAL_SIGN_IN : ROUTES.SIGN_IN} />
		);
	}

	const props = {
		user
	};

	return (
		<div id="outer-container">
			<ToastManager />
			<InactivityScreen />
			<ContextProvider>
				<Router>
					<Routes>
						<Route path={ROUTES.BI} element={<BI {...props} />} />
						<Route path={ROUTES.HOME} element={<Home {...props} />} />
						{ isAdminUser && <Route path={ROUTES.INTEGRATIONS} element={<Integrations {...props} />} /> }
						{ isAdminUser && <Route path={ROUTES.HUBSPOT_INTEGRATION} element={<HubspotIntegration {...props} />} /> }
						{ isAdminUser && <Route path={ROUTES.SALESFORCE_INTEGRATION} element={<SalesforceIntegration {...props} />} /> }
						<Route path={ROUTES.SALES} element={<Sales {...props} />} />
						{ !ext(user) ? (
							<>
								<Route path={ROUTES.DATA} element={<Data {...props} />} />
								<Route path={ROUTES.MAPPING} element={<Mapping {...props} />} />
								<Route path={ROUTES.ACCOUNTS} element={<Accounts {...props} />} />
								<Route path={ROUTES.CONTACTS} element={<Contacts {...props} />} />
								<Route path={ROUTES.ACCOUNT} element={<EntityEditor {...props} />} />
								<Route path={ROUTES.CONTACT} element={<EntityEditor {...props} />} />
							</>
						) : (
							<>
								<Route path={ROUTES.DATA} element={<ProtectedRouteRedirect />} />
								<Route path={ROUTES.MAPPING} element={<ProtectedRouteRedirect />} />
								<Route path={ROUTES.ACCOUNTS} element={<ProtectedRouteRedirect />} />
								<Route path={ROUTES.CONTACTS} element={<ProtectedRouteRedirect />} />
								<Route path={ROUTES.ACCOUNT} element={<ProtectedRouteRedirect />} />
								<Route path={ROUTES.CONTACT} element={<ProtectedRouteRedirect />} />
							</>
						)}
						<Route path={ROUTES.PARTNERSHIP} element={<Partnership {...props} />} />

						{ isAdminUser && <Route path={ROUTES.SETTINGS} element={<Settings {...props} />} /> }
						{ isAdminUser && <Route path={ROUTES.USERS} element={<UserManagement {...props} />} /> }
						{ isAdminUser && <Route path={ROUTES.LICENSE} element={<LicenseManagement {...props} />} /> }
						{ isAdminUser && <Route path={ROUTES.DBSNAPSHOTS} element={<DBSnapshots {...props} />} /> }
						{ isAdminUser && <Route path={ROUTES.SALESFORCE_MANAGERS} element={<SalesforceManagers {...props} />} /> }
						{ isAdminUser && <Route path={ROUTES.TEST} element={<Test {...props} />} /> }

						<Route path={ROUTES.DASHBOARD_REPORT} element={<DashboardReport {...props} />} />

						<Route path={ROUTES.OPPORTUNITIES} element={<Opportunities {...props} />} />
						<Route path={ROUTES.OPPORTUNITY} element={<ViewOpportunity {...props} />} />

						<Route path={ROUTES.CO_SELL_OPPORTUNITIES} element={<CoSellOpportunities {...props} />} />
						<Route path={ROUTES.CO_SELL_OPPORTUNITY} element={<ViewCoSellOpportunity {...props} />} />

						<Route path={ROUTES.HEATMAPS} element={<Heatmaps {...props} />} />
						<Route path={ROUTES.HEATMAP} element={<ViewHeatmap {...props} />} />

						<Route path={ROUTES.CAMPAIGNS} element={<Campaigns {...props} />} />
						<Route path={ROUTES.CAMPAIGN} element={<ViewCampaign {...props} />} />
						<Route path={ROUTES.CAMPAIGN_MEMBERS} element={<CampaignMembers {...props} />} />

						<Route path={ROUTES.PROGRAMS} element={<Programs {...props} />} />
						<Route path={ROUTES.PROGRAM} element={<EditPrograms {...props} />} />

						<Route path={ROUTES.FUNDS} element={<Funds {...props} />} />
						<Route path={ROUTES.FUND} element={<EditFunds {...props} />} />

						<Route path={ROUTES.MARKETINGS} element={<Marketing {...props} />} />
						<Route path={ROUTES.MARKETING} element={<EditMarketing {...props} />} />

						<Route path="*" element={<Navigate to={ROUTES.HOME} />} />
					</Routes>
				</Router>
			</ContextProvider>
		</div>
	)
};

export default App;
